import { HISTORIC_SITES } from './historicSitesData.js';

export const SITE_BY_ID = new Map(HISTORIC_SITES.map(site => [site.id, site]));

// Questions use the geography and history already described in the site records.
export const OBSERVATIONS = {
  'edo-castle': {
    prompt: '城跡のまわりを囲む、曲がった水の帯。どんな役割があっただろう？',
    hint: '街と城の境界に注目。近づく相手の通り道を考えてみよう。',
    choices: ['船が海へ出るための運河', '敵の接近を妨げる堀', '田畑に水を送る水路'], answer: 1,
    explain: '城を囲む堀は防御のための障害になりました。東京の街の中にも、城を中心につくられた区画が残っています。',
  },
  zojoji: {
    prompt: '増上寺の後ろに見えた東京タワー。寺と塔の年代の関係は？',
    hint: '増上寺は徳川家と関わる寺。都市の景色に重なる時代を想像しよう。',
    choices: ['寺が先にあり、後の時代に塔が加わった', '塔のために新しく寺を建てた', '寺と塔は同時につくられた'], answer: 0,
    explain: '増上寺は1598年に芝へ移りました。後の時代の東京タワーと並ぶ景色から、一つの街に異なる時代が重なることを読み取れます。',
  },
  'meiji-jingu': {
    prompt: '市街地の中に広がる明治神宮の森。この緑はどうやって生まれた？',
    hint: '神社の創建は1920年。人が未来の風景をつくることもある。',
    choices: ['最近まで海だった場所に自然に生えた', '太古から人が一度も手を入れていない', '人々が木を植え、長い年月をかけて育てた'], answer: 2,
    explain: '全国から寄せられた木を植えて育てた人工林です。大きな緑のまとまりも、人々の計画と手入れによって生まれます。',
  },
  kinkakuji: {
    prompt: '池と建物が組み合わされた金閣寺。建物を取り巻く景色は何の手がかりになる？',
    hint: 'ここは足利義満の山荘が前身。眺める場所と見られる建物の関係に注目。',
    choices: ['建物と庭園を一緒に楽しむ文化', '工業製品を運ぶための港', '飛行機の離着陸のための施設'], answer: 0,
    explain: '金閣寺は義満の山荘を前身とします。建物だけでなく池や庭園との組合せも、権力者の暮らしと文化を考える手がかりです。',
  },
  kiyomizudera: {
    prompt: '清水寺の舞台があるのは、京都のどんな地形？',
    hint: '寺の背後と、街が広がる方向を見比べよう。',
    choices: ['海上の人工島', '盆地の中心の平地', '東山の斜面'], answer: 2,
    explain: '清水寺は京都の東側、東山の斜面にあります。舞台の柱は斜面から組み上げられ、地形と木造建築が結び付いています。',
  },
  'eiffel-tower': {
    prompt: '川や広場に囲まれた鉄の塔。どんな時代の技術を伝える建築？',
    hint: '石や木でできた城・寺院との違いに注目してみよう。',
    choices: ['近代の工業技術と万国博覧会', '古代の石積みの墓', '中世の木造の見張り台'], answer: 0,
    explain: '1889年の万国博覧会に合わせて建てられ、鉄の部材を組み合わせる工業技術を示しました。王立展示館との比較もおすすめです。',
  },
  bastille: {
    prompt: 'バスティーユの場所には今、広場がある。歴史を読むとき何に注意する？',
    hint: '歴史的な出来事の場所に、当時の建物が残っているとは限らない。',
    choices: ['広場なら歴史上の出来事は起きていない', '現在の景色と当時の姿を区別する', '今ある建物はすべて当時のものと考える'], answer: 1,
    explain: '1789年の襲撃後に牢獄は取り壊されました。現在の広場から当時の建物を想像するには、記録や図を合わせて読む必要があります。',
  },
  'sendai-castle': {
    prompt: '川と崖に近い高台に、仙台城を築いた利点は？',
    hint: '遠くを見渡せる場所、相手が登りにくい場所を考えよう。',
    choices: ['周囲より低く洪水に浸かりやすい', '海の中に船を隠せる', '地形を防御に利用できる'], answer: 2,
    explain: '青葉山の高台と広瀬川の崖は防御に役立ちました。城の位置を地形と結び付けると、築城の理由が見えてきます。',
  },
  'edinburgh-castle': {
    prompt: '城の北側に広がる整った街路。旧市街と比べて何が違う？',
    hint: '尾根に沿う旧市街と、計画的に広がる新市街を見比べよう。',
    choices: ['どちらも同じ計画で同時にできた', '新市街は計画的に整備された', '新市街には歴史的な建築がない'], answer: 1,
    explain: '新市街は18世紀以降に計画的に整えられました。「新しい」という名でも数百年の歴史があります。',
  },
};

export const COURSES = [
  {
    id: 'tokyo-through-time', name: '江戸から近代の東京へ', region: '東京', emoji: '🏯', duration: '5〜10分',
    description: '城、将軍家の寺、近代の森。街に重なる三つの時代を探す。',
    siteIds: ['edo-castle', 'zojoji', 'meiji-jingu'],
    departure: { name: '東京', lat: 35.6812, lng: 139.7671, elevation: 10 },
    reflection: '城の堀、寺、森。東京の景色に残る人の工夫を一つ、自分の言葉で書いてみよう。',
  },
  {
    id: 'kyoto-landscape', name: '京都・庭園と山の寺', region: '京都', emoji: '🌳', duration: '5〜10分',
    description: '金閣寺から清水寺へ。池と斜面、建築と自然の関係を見比べる。',
    siteIds: ['kinkakuji', 'kiyomizudera'],
    departure: { name: '京都', lat: 35.0270, lng: 135.7370, elevation: 60 },
    reflection: '金閣寺と清水寺では、建物と自然の関係にどんな違いがあった？',
  },
  {
    id: 'paris-change', name: 'パリ・街が伝える変化', region: 'パリ', emoji: '🗼', duration: '5〜10分',
    description: '鉄の塔と牢獄の跡。残る建築と、失われた建築から歴史を考える。',
    siteIds: ['eiffel-tower', 'bastille'],
    departure: { name: 'パリ', lat: 48.8636, lng: 2.2816, elevation: 60 },
    reflection: '残っている塔と失われた牢獄。現在の景色だけでは分からないことは何だろう？',
  },
];

export const COMPARISONS = [
  { name: '万国博覧会と技術', ids: ['eiffel-tower', 'royal-exhibition-building'], prompt: '塔と展示館。博覧会で技術や繁栄を伝える方法に、どんな違いがある？' },
  { name: '城と地形', ids: ['sendai-castle', 'edinburgh-castle'], prompt: '川、崖、高台。遠く離れた二つの城に共通する地形の使い方を探そう。' },
  { name: '近世の政治と都市', ids: ['edo-castle', 'isfahan-imam-square'], prompt: '城を囲む堀と、広場を囲む建築。政治と人々の暮らしはどう結び付いていた？' },
  { name: '人が育てた緑', ids: ['meiji-jingu', 'singapore-botanic-gardens'], prompt: 'どちらも人が育てた緑。つくられた目的と、社会で果たした役割を比べよう。' },
];

// Century numbers are sort keys, never displayed as invented exact years.
export function timelineStart(site) {
  const label = site.yearLabel.replace(/^現在の建物は/, '');
  const century = label.match(/^(前)?(\d+)(?:〜(?:前)?\d+)?世紀/);
  if (century) return century[1] ? -Number(century[2]) * 100 : (Number(century[2]) - 1) * 100 + 1;
  const year = label.match(/^(前)?(\d+)(?=年|〜)/);
  return year ? Number(year[2]) * (year[1] ? -1 : 1) : null;
}

export function courseState(course, progress) {
  const saved = progress.getCourse(course.id);
  const nextIndex = Math.max(0, Math.min(course.siteIds.length, Number.isInteger(saved?.nextIndex) ? saved.nextIndex : 0));
  return { nextIndex, completedAt: saved?.completedAt ?? null, reflection: typeof saved?.reflection === 'string' ? saved.reflection : '' };
}

export function createCourseSession(course, progress, { restart = false } = {}) {
  const saved = courseState(course, progress);
  let state = restart ? { ...saved, nextIndex: 0, completedAt: null } : saved;
  if (restart) progress.saveCourse(course.id, state);
  return {
    get nextIndex() { return state.nextIndex; },
    get targetId() { return course.siteIds[state.nextIndex] ?? null; },
    get complete() { return state.nextIndex === course.siteIds.length; },
    visit(id) {
      if (id !== this.targetId) return false;
      state = { ...state, nextIndex: state.nextIndex + 1 };
      if (this.complete) state.completedAt = Date.now();
      progress.saveCourse(course.id, state);
      return true;
    },
  };
}
